// Primitives de tracé partagées par les peintres du décor (ui/sceneryPainter.js, ui/groundPainter.js).
// Toutes les formes sont ajoutées à des Path2D, puis remplies d'un coup par couleur.

export const TAU = Math.PI * 2;

// --- Primitives de tracé -----------------------------------------------------------------
// Toutes les formes sont tracées dans le même sens (horaire à l'écran) : avec la règle de
// remplissage « nonzero », deux formes qui se chevauchent ne creusent jamais de trou.

/**
 * Contour fermé. Chaque nœud est [x, y] (segment droit depuis le précédent) ou [x, y, cx, cy]
 * (courbe quadratique de point de contrôle (cx, cy) qui arrive à ce nœud ; sur le premier nœud,
 * c'est la courbe qui referme le contour).
 */
export function outline(path, nodes) {
  const pts = nodes.length > 2 || nodes.some((n) => n.length > 2) ? nodes : null;
  if (!pts) return;
  const ordered = signedArea(pts) < 0 ? reversed(pts) : pts;
  path.moveTo(ordered[0][0], ordered[0][1]);
  for (let i = 1; i <= ordered.length; i++) {
    const n = ordered[i % ordered.length];
    if (n.length > 2) path.quadraticCurveTo(n[2], n[3], n[0], n[1]);
    else if (i < ordered.length) path.lineTo(n[0], n[1]);
  }
  path.closePath();
}

function signedArea(nodes) {
  const pts = [];
  for (const n of nodes) {
    if (n.length > 2) pts.push([n[2], n[3]]);
    pts.push(n);
  }
  let sum = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % pts.length];
    sum += x1 * y2 - x2 * y1;
  }
  return sum;
}

/** Même contour parcouru à l'envers : la courbe qui arrivait au suivant arrive désormais ici. */
function reversed(nodes) {
  const n = nodes.length;
  return nodes.map((_, i) => {
    const node = nodes[(n - i) % n];
    const next = nodes[(n - i + 1) % n];
    return next.length > 2 ? [node[0], node[1], next[2], next[3]] : [node[0], node[1]];
  });
}

export const poly = outline;

export function disc(path, cx, cy, r) {
  path.moveTo(cx + r, cy);
  path.arc(cx, cy, r, 0, TAU);
}

export function oval(path, cx, cy, rx, ry, rot = 0) {
  path.moveTo(cx + rx * Math.cos(rot), cy + rx * Math.sin(rot));
  path.ellipse(cx, cy, rx, ry, rot, 0, TAU);
}

/** Membre, branche ou tige : quadrilatère de a à b, de largeur w1 à w2. */
export function limb(path, [x1, y1], [x2, y2], w1, w2 = w1) {
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const nx = -(y2 - y1) / len;
  const ny = (x2 - x1) / len;
  poly(path, [
    [x1 + (nx * w1) / 2, y1 + (ny * w1) / 2], [x2 + (nx * w2) / 2, y2 + (ny * w2) / 2],
    [x2 - (nx * w2) / 2, y2 - (ny * w2) / 2], [x1 - (nx * w1) / 2, y1 - (ny * w1) / 2],
  ]);
}

/**
 * Repère local d'un élément : at(u, v) → [x, y] écran, u de 0 (gauche) à 1 (droite) — en miroir si
 * l'élément est tourné vers la gauche —, v de 0 (sol) à 1 (sommet), penché de p.skew.
 */
export function frame(x, base, w, h, p) {
  const skew = p.skew || 0;
  return (u, v) => [x + ((p.flip ? 1 - u : u) + v * skew) * w, base - v * h];
}

/** Yeux qui clignent de temps en temps (fermés ~0,2 s toutes les 5 s, chacun à son rythme). */
export const eyesOpen = (t, phase = 0) => (t + phase) % 5 > 0.2;

export function eyes(path, cx, cy, gap, r) {
  disc(path, cx - gap / 2, cy, r);
  disc(path, cx + gap / 2, cy, r);
}

/** Voûte : rectangle surmonté d'une demi-ellipse de hauteur `r`. */
export function arch(path, x, y, w, h, r = Math.min(w / 2, h * 0.6)) {
  path.moveTo(x, y + h);
  path.lineTo(x, y + r);
  path.ellipse(x + w / 2, y + r, w / 2, r, 0, Math.PI, 0);
  path.lineTo(x + w, y + h);
  path.closePath();
}
